import { useState, useEffect, useRef } from "react";

const gold = "#f5a623", blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e", red = "#ef4444", purple = "#a78bfa";
const navy = "#0b0f1a", navy2 = "#111827", navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}
function Reveal({ children, delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(22px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const ss = {
  section: { marginBottom: 72 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  line: { flex: 1, height: 1, background: border },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 900, color: white, lineHeight: 1.25, marginBottom: 20 },
  prose: { fontSize: "0.97rem", color: textCol, lineHeight: 1.85, marginBottom: 28 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "48px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "28px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function Pill({ text, color = gold }) {
  return <span style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color, background: `${color}18`, border: `1px solid ${color}33`, marginRight: 4, marginBottom: 4 }}>{text}</span>;
}

// ── S1: Benchmarks ──
function BenchmarkTable() {
  const [active, setActive] = useState(null);
  const benchmarks = [
    { fund: "Canadian Equity Fund", benchmark: "S&P/TSX Composite Index", color: red, flag: "🍁", desc: "Tracks ~250 large-cap Canadian companies across all sectors. The primary gauge of the Canadian stock market's performance." },
    { fund: "U.S. Equity Fund", benchmark: "S&P 500 Index", color: blue2, flag: "🇺🇸", desc: "Tracks the 500 largest U.S. publicly traded companies by market cap. The world's most widely followed equity benchmark." },
    { fund: "Global Equity Fund", benchmark: "MSCI World Index", color: gold, flag: "🌍", desc: "Covers ~1,500 large and mid-cap stocks across 23 developed market countries. Represents global developed-market equity exposure." },
    { fund: "Canadian Bond Fund", benchmark: "FTSE Canada Universe Bond Index", color: green, flag: "📄", desc: "Tracks investment-grade Canadian bonds (government and corporate) with terms > 1 year. The standard benchmark for Canadian fixed income." },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gap: 8 }}>
        {benchmarks.map((row, i) => (
          <div key={i} onClick={() => setActive(active === i ? null : i)}
            style={{ background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? row.color + "55" : border}`, borderRadius: 8, padding: "16px 20px", cursor: "pointer", transition: "all 0.2s" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: "1.3rem" }}>{row.flag}</span>
                <div>
                  <div style={{ fontSize: "0.86rem", fontWeight: 700, color: white }}>{row.fund}</div>
                  <div style={{ fontSize: "0.78rem", color: row.color, marginTop: 2 }}>vs. {row.benchmark}</div>
                </div>
              </div>
              <span style={{ color: active === i ? row.color : muted, fontSize: "1rem", display: "inline-block", transform: active === i ? "rotate(90deg)" : "none", transition: "transform 0.25s" }}>›</span>
            </div>
            {active === i && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${border}`, fontSize: "0.84rem", color: textCol, lineHeight: 1.7 }}>{row.desc}</div>
            )}
          </div>
        ))}
      </div>

      {/* Relative performance visualizer */}
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24, marginTop: 14 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 14 }}>📊 Relative Performance Visualizer</div>
        <RelativePerformanceViz />
      </div>
    </div>
  );
}

function RelativePerformanceViz() {
  const [fundReturn, setFundReturn] = useState(8);
  const [benchReturn, setBenchReturn] = useState(12);
  const diff = (parseFloat(fundReturn) - parseFloat(benchReturn)).toFixed(1);
  const outperformed = parseFloat(diff) >= 0;
  const maxBar = Math.max(Math.abs(parseFloat(fundReturn)), Math.abs(parseFloat(benchReturn)), 1);

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        {[["Fund Return (%)", fundReturn, setFundReturn, blue2], ["Benchmark Return (%)", benchReturn, setBenchReturn, gold]].map(([lbl, val, set, col], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" step="0.1" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
        {[{ label: "Fund", val: fundReturn, color: blue2 }, { label: "Benchmark", val: benchReturn, color: gold }].map((row, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: row.color, width: 70, flexShrink: 0 }}>{row.label}</span>
            <div style={{ flex: 1, height: 18, background: navy4, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${(Math.abs(parseFloat(row.val)) / (maxBar * 1.2)) * 100}%`, background: row.color, borderRadius: 3, transition: "width 0.4s ease", minWidth: 2 }} />
            </div>
            <span style={{ fontFamily: "monospace", fontSize: "0.82rem", color: white, width: 45, textAlign: "right" }}>{parseFloat(row.val).toFixed(1)}%</span>
          </div>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${outperformed ? green : red}33`, borderRadius: 8, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: "1.4rem" }}>{outperformed ? "✅" : "⚠️"}</span>
        <div>
          <div style={{ fontSize: "0.82rem", fontWeight: 700, color: outperformed ? green : gold }}>
            Fund {outperformed ? "Outperformed" : "Underperformed"} benchmark by {Math.abs(parseFloat(diff))}%
          </div>
          <div style={{ fontSize: "0.78rem", color: muted, marginTop: 2 }}>
            {outperformed ? "Generated positive alpha — added value above the benchmark." : `A ${parseFloat(fundReturn) > 0 ? "positive return is not enough" : "negative return"} — the benchmark return sets the real bar, not zero.`}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── S2: Rates of Return ──
function SimpleReturnCalc() {
  const [begin, setBegin] = useState(5000);
  const [end, setEnd] = useState(5200);
  const [dist, setDist] = useState(100);
  const [result, setResult] = useState(null);

  function calc() {
    const b = parseFloat(begin), e = parseFloat(end), d = parseFloat(dist);
    const gain = (e - b) + d;
    const ret = (gain / b * 100).toFixed(2);
    setResult({ b, e, d, gain, ret });
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 26, margin: "16px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 6 }}>🧮 Simple Rate of Return</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "6px 14px", marginBottom: 16, display: "inline-block" }}>
        Return = [(Ending Value − Beginning Value + Distributions) ÷ Beginning Value] × 100
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10, marginBottom: 14 }}>
        {[["Beginning Value ($)", begin, setBegin], ["Ending Value ($)", end, setEnd], ["Distributions ($)", dist, setDist]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" step="any" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "11px 24px", fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
      {result && (
        <div style={{ background: navy4, border: "1px solid rgba(245,166,35,.2)", borderRadius: 8, padding: 16, marginTop: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: gold, marginBottom: 2 }}>Simple Return</div>
              <div style={{ fontFamily: "monospace", fontSize: "2rem", fontWeight: 900, color: parseFloat(result.ret) >= 0 ? green : red }}>{result.ret}%</div>
            </div>
          </div>
          {[
            `Capital gain: $${result.e.toLocaleString()} − $${result.b.toLocaleString()} = $${(result.e - result.b).toLocaleString()}`,
            `Total gain: $${(result.e - result.b).toLocaleString()} + $${result.d} distributions = $${result.gain.toLocaleString()}`,
            `Return: $${result.gain.toLocaleString()} ÷ $${result.b.toLocaleString()} × 100 = ${result.ret}%`,
          ].map((step, i) => (
            <div key={i} style={{ fontFamily: "monospace", fontSize: "0.79rem", color: textCol, padding: "3px 0", display: "flex", gap: 8 }}>
              <span style={{ color: gold, flexShrink: 0 }}>→</span>{step}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TWRRvsMWRR() {
  const [scenario, setScenario] = useState("bad_timing");
  const scenarios = {
    bad_timing: {
      label: "Bad Timing — Adds Before Crash",
      color: red,
      twrr: "10.0%",
      mwrr: "3.2%",
      icon: "📉",
      timeline: [
        { event: "Jan 1 — Invests $10,000", note: "Fund worth $10,000" },
        { event: "Jun 30 — Fund up 20%", note: "Worth $12,000. Adds another $20,000 (total $32,000)" },
        { event: "Dec 31 — Fund drops 5%", note: "Final value: $30,400" },
      ],
      explanation: "The manager achieved a good overall return (+10% TWRR). But the client added $20,000 just before the downturn — their personal experience (MWRR) was only 3.2%. The client's timing hurt them, not the manager.",
    },
    good_timing: {
      label: "Good Timing — Adds Before Rally",
      color: green,
      twrr: "10.0%",
      mwrr: "14.8%",
      icon: "📈",
      timeline: [
        { event: "Jan 1 — Invests $10,000", note: "Fund worth $10,000" },
        { event: "Jun 30 — Fund down 5%", note: "Worth $9,500. Adds another $20,000 (total $29,500)" },
        { event: "Dec 31 — Fund rallies 15%", note: "Final value: $33,925" },
      ],
      explanation: "Same fund, same manager, same 10% TWRR. But this client added money before the rally — their MWRR was 14.8%, better than the fund's actual return. Their timing helped them.",
    },
  };
  const sc = scenarios[scenario];

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 26, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>⚖️ TWRR vs. MWRR — Interactive Scenario</div>
      <p style={{ fontSize: "0.85rem", color: muted, lineHeight: 1.65, marginBottom: 18 }}>
        The fund manager's <strong style={{ color: blue2 }}>TWRR stays constant at 10%</strong> in both scenarios — their skill doesn't change. Only the <strong style={{ color: gold }}>client's deposit timing</strong> changes, which affects only the MWRR.
      </p>
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {Object.entries(scenarios).map(([key, val]) => (
          <button key={key} onClick={() => setScenario(key)} style={{ flex: 1, padding: "10px 12px", background: scenario === key ? navy4 : navy3, border: `1px solid ${scenario === key ? val.color + "55" : border}`, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: "0.76rem", fontWeight: 700, color: scenario === key ? val.color : muted, transition: "all 0.2s" }}>{val.icon} {val.label}</button>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        {[{ label: "TWRR (Manager's Performance)", val: sc.twrr, color: blue2, sub: "Ignores client cash flows" }, { label: "MWRR (Client's Experience)", val: sc.mwrr, color: sc.color, sub: "Reflects deposit timing" }].map((card, i) => (
          <div key={i} style={{ background: navy4, border: `1px solid ${card.color}33`, borderRadius: 8, padding: "14px 16px", textAlign: "center" }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: card.color, marginBottom: 4 }}>{card.label}</div>
            <div style={{ fontFamily: "monospace", fontSize: "1.6rem", fontWeight: 900, color: white }}>{card.val}</div>
            <div style={{ fontSize: "0.7rem", color: muted, marginTop: 4 }}>{card.sub}</div>
          </div>
        ))}
      </div>
      <div style={{ marginBottom: 14 }}>
        {sc.timeline.map((item, i) => (
          <div key={i} style={{ display: "flex", gap: 12, marginBottom: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: navy4, border: `2px solid ${sc.color}44`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: sc.color, flexShrink: 0 }}>{i + 1}</div>
            <div style={{ paddingTop: 4 }}>
              <div style={{ fontSize: "0.82rem", fontWeight: 700, color: white }}>{item.event}</div>
              <div style={{ fontSize: "0.78rem", color: muted }}>{item.note}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ background: navy4, borderLeft: `3px solid ${sc.color}`, borderRadius: 6, padding: "12px 16px", fontSize: "0.84rem", color: textCol, lineHeight: 1.7 }}>{sc.explanation}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
        {[
          { label: "TWRR Used For", items: ["Fund Facts performance reporting", "Advertising & marketing", "Benchmarking manager skill", "Industry standard"], color: blue2 },
          { label: "MWRR Used For", items: ["Client account statements (CRM2)", "Reflects personal return experience", "Accounts for cash flow timing", "Like an IRR calculation"], color: gold },
        ].map((card, i) => (
          <div key={i} style={{ background: navy3, border: `1px solid ${card.color}33`, borderRadius: 8, padding: "14px 16px" }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: card.color, marginBottom: 8 }}>{card.label}</div>
            {card.items.map((item, j) => <div key={j} style={{ fontSize: "0.78rem", color: textCol, marginBottom: 4 }}>→ {item}</div>)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── S3: Risk-Adjusted Measures ──
function RiskMeasuresExplorer() {
  const [active, setActive] = useState(0);
  const measures = [
    {
      icon: "📊", name: "Standard Deviation", color: blue2, badge: "Volatility",
      definition: "Measures how much the fund's returns fluctuate around its average. The wider the spread of returns, the higher the standard deviation.",
      scale: [
        { label: "Money Market", sd: "< 0.5%", color: green, risk: "Low" },
        { label: "Bond Fund", sd: "2–5%", color: blue2, risk: "Low-Med" },
        { label: "Canadian Equity", sd: "10–15%", color: gold, risk: "Medium" },
        { label: "Emerging Markets", sd: "20–30%", color: red, risk: "High" },
      ],
      usage: "Used on Fund Facts to assign a Risk Rating — Low, Low-to-Medium, Medium, Medium-to-High, or High.",
      pills: ["Total Risk", "Volatility Measure", "Fund Facts Rating", "Historical"],
    },
    {
      icon: "📐", name: "Beta", color: gold, badge: "Market Sensitivity",
      definition: "Measures how a fund moves relative to its benchmark index. Beta = 1.0 means moves exactly with the market. Beta > 1 is more volatile; Beta < 1 is less volatile.",
      scale: [
        { label: "Defensive Fund", sd: "Beta 0.6", color: green, risk: "20% less volatile" },
        { label: "Index Fund", sd: "Beta 1.0", color: blue2, risk: "Matches market" },
        { label: "Growth Fund", sd: "Beta 1.3", color: gold, risk: "30% more volatile" },
        { label: "Aggressive Fund", sd: "Beta 1.8", color: red, risk: "80% more volatile" },
      ],
      usage: "Useful for understanding how much market risk a fund adds to a portfolio. Does NOT capture non-market (company-specific) risk.",
      pills: ["Market Risk", "Relative Volatility", "Benchmark-Linked", "Systematic Risk"],
    },
    {
      icon: "⚡", name: "Sharpe Ratio", color: green, badge: "Risk-Adjusted Return",
      definition: "Measures excess return per unit of total risk. How much additional return did the manager earn above the risk-free rate (T-bill) for every unit of standard deviation?",
      scale: [
        { label: "Poor", sd: "< 0.5", color: red, risk: "Low return for risk taken" },
        { label: "Acceptable", sd: "0.5–1.0", color: gold, risk: "Reasonable trade-off" },
        { label: "Good", sd: "1.0–2.0", color: blue2, risk: "Strong risk-adjusted return" },
        { label: "Excellent", sd: "> 2.0", color: green, risk: "Outstanding efficiency" },
      ],
      usage: "Higher is always better. Allows comparison across funds with different risk levels — levels the playing field.",
      pills: ["Risk-Adjusted", "Excess Return", "Higher = Better", "Cross-Fund Comparison"],
    },
  ];
  const m = measures[active];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 4, marginBottom: 0, flexWrap: "wrap" }}>
        {measures.map((ms, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ padding: "10px 18px", background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? ms.color + "55" : border}`, borderBottom: active === i ? `2px solid ${ms.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: active === i ? ms.color : muted, transition: "all 0.2s", whiteSpace: "nowrap" }}>
            {ms.icon} {ms.name}
          </button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${m.color}22`, borderRadius: "0 0 8px 8px", padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <span style={{ fontSize: "1.6rem" }}>{m.icon}</span>
          <div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", color: white }}>{m.name}</div>
            <span style={{ fontSize: "0.65rem", fontWeight: 700, padding: "2px 10px", borderRadius: 100, color: m.color, background: `${m.color}18`, border: `1px solid ${m.color}33` }}>{m.badge}</span>
          </div>
        </div>
        <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75, marginBottom: 16 }}>{m.definition}</div>
        <div style={{ display: "grid", gap: 6, marginBottom: 14 }}>
          {m.scale.map((row, i) => (
            <div key={i} style={{ background: navy3, borderRadius: 6, padding: "9px 14px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: "0.8rem", fontWeight: 700, color: white, minWidth: 120 }}>{row.label}</span>
              <span style={{ fontFamily: "monospace", fontSize: "0.78rem", color: row.color, background: `${row.color}12`, border: `1px solid ${row.color}25`, padding: "2px 8px", borderRadius: 4 }}>{row.sd}</span>
              <span style={{ fontSize: "0.75rem", color: muted }}>{row.risk}</span>
            </div>
          ))}
        </div>
        <div style={{ background: navy3, borderLeft: `3px solid ${m.color}`, borderRadius: 6, padding: "11px 16px", fontSize: "0.82rem", color: muted, lineHeight: 1.7, marginBottom: 12 }}>💡 {m.usage}</div>
        <div>{m.pills.map((p, i) => <Pill key={i} text={p} color={m.color} />)}</div>
      </div>
    </div>
  );
}

function SharpeCalc() {
  const [fundReturn, setFundReturn] = useState(12);
  const [riskFree, setRiskFree] = useState(2);
  const [stdDev, setStdDev] = useState(8);
  const [result, setResult] = useState(null);

  function calc() {
    const f = parseFloat(fundReturn), r = parseFloat(riskFree), s = parseFloat(stdDev);
    const excess = f - r;
    const sharpe = (excess / s).toFixed(2);
    const rating = parseFloat(sharpe) > 2 ? { label: "Excellent", color: green }
      : parseFloat(sharpe) > 1 ? { label: "Good", color: blue2 }
      : parseFloat(sharpe) > 0.5 ? { label: "Acceptable", color: gold }
      : { label: "Poor", color: red };
    setResult({ f, r, s, excess, sharpe, rating });
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 26, margin: "16px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 6 }}>🧮 Sharpe Ratio Calculator</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "6px 14px", marginBottom: 16, display: "inline-block" }}>
        Sharpe Ratio = (Fund Return − Risk-Free Rate) ÷ Standard Deviation
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10, marginBottom: 14 }}>
        {[["Fund Return (%)", fundReturn, setFundReturn], ["Risk-Free Rate (%)", riskFree, setRiskFree], ["Std Deviation (%)", stdDev, setStdDev]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" step="0.1" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.9rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "11px 24px", fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
      {result && (
        <div style={{ background: navy4, border: `1px solid ${result.rating.color}33`, borderRadius: 8, padding: 18, marginTop: 14 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: gold, marginBottom: 2 }}>Sharpe Ratio</div>
              <div style={{ fontFamily: "monospace", fontSize: "2.2rem", fontWeight: 900, color: white }}>{result.sharpe}</div>
            </div>
            <span style={{ fontSize: "0.75rem", fontWeight: 700, padding: "6px 16px", borderRadius: 100, color: result.rating.color, background: `${result.rating.color}18`, border: `1px solid ${result.rating.color}33`, alignSelf: "flex-start", marginTop: 6 }}>{result.rating.label}</span>
          </div>
          {[
            `Excess return: ${result.f}% − ${result.r}% = ${result.excess}%`,
            `Sharpe Ratio: ${result.excess}% ÷ ${result.s}% = ${result.sharpe}`,
            `Interpretation: The manager earned ${result.sharpe} units of return for every 1 unit of risk.`,
          ].map((step, i) => (
            <div key={i} style={{ fontFamily: "monospace", fontSize: "0.79rem", color: i === 2 ? gold : textCol, padding: "3px 0", display: "flex", gap: 8 }}>
              <span style={{ color: gold, flexShrink: 0 }}>→</span>{step}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RealReturnCalc() {
  const [nominal, setNominal] = useState(4.5);
  const [inflation, setInflation] = useState(3.0);
  const real = (parseFloat(nominal) - parseFloat(inflation)).toFixed(2);
  const positive = parseFloat(real) > 0;

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 26, margin: "16px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 6 }}>🧮 Real (Inflation-Adjusted) Return</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "6px 14px", marginBottom: 16, display: "inline-block" }}>
        Real Return = Nominal Return − Inflation Rate
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
        {[["Nominal Return (%)", nominal, setNominal], ["Inflation Rate (%)", inflation, setInflation]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" step="0.1" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.9rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${positive ? green : red}33`, borderRadius: 8, padding: "16px 18px" }}>
        <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: gold, marginBottom: 4 }}>Real Return</div>
        <div style={{ fontFamily: "monospace", fontSize: "2rem", fontWeight: 900, color: positive ? green : red }}>{real}%</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.79rem", color: textCol, marginTop: 10, borderTop: `1px solid ${border}`, paddingTop: 8 }}>
          → {parseFloat(nominal).toFixed(1)}% − {parseFloat(inflation).toFixed(1)}% = {real}%<br />
          → {positive ? `Purchasing power grew by ${real}% in real terms.` : `Purchasing power ERODED — inflation exceeded the fund's return.`}
        </div>
      </div>
    </div>
  );
}

// ── S4: Quartile Rankings ──
function QuartileViz() {
  const [fundPct, setFundPct] = useState(80);
  const quartile = fundPct >= 75 ? 1 : fundPct >= 50 ? 2 : fundPct >= 25 ? 3 : 4;
  const labels = ["", "1st Quartile — Top 25%", "2nd Quartile — Above Average", "3rd Quartile — Below Average", "4th Quartile — Bottom 25%"];
  const colors = ["", green, blue2, gold, red];
  const descriptions = [
    "",
    "Top performer — better than 75% of peers. Strong manager skill or strategy.",
    "Above average — better than 50% of peers. Consistently good performance.",
    "Below average — better than only 25% of peers. Room for improvement.",
    "Weakest performers — bottom 25%. High risk of being merged or liquidated.",
  ];

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 26, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>📈 Quartile Ranking Explorer</div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 20, lineHeight: 1.65 }}>
        Drag the slider to set a fund's percentile rank among its peers and see which quartile it lands in.
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <label style={{ fontSize: "0.72rem", fontWeight: 700, color: muted, textTransform: "uppercase", letterSpacing: "0.1em", flexShrink: 0 }}>Percentile Rank</label>
        <input type="range" min={1} max={100} value={fundPct} onChange={e => setFundPct(parseInt(e.target.value))} style={{ flex: 1, accentColor: colors[quartile] }} />
        <span style={{ fontFamily: "monospace", fontSize: "1rem", color: colors[quartile], width: 55, textAlign: "right" }}>Top {100 - fundPct + 1}%</span>
      </div>
      {/* 4-block bar */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6, marginBottom: 14 }}>
        {[4, 3, 2, 1].map(q => (
          <div key={q} style={{ background: quartile === q ? `${colors[q]}22` : navy4, border: `1px solid ${quartile === q ? colors[q] + "66" : border}`, borderRadius: 6, padding: "12px 10px", textAlign: "center", transition: "all 0.3s" }}>
            <div style={{ fontFamily: "monospace", fontSize: "0.65rem", fontWeight: 700, color: quartile === q ? colors[q] : muted, marginBottom: 4 }}>Q{q}</div>
            <div style={{ fontSize: "0.72rem", color: quartile === q ? white : muted, fontWeight: quartile === q ? 700 : 400 }}>{q === 1 ? "Top 25%" : q === 2 ? "26–50%" : q === 3 ? "51–75%" : "Bottom 25%"}</div>
          </div>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${colors[quartile]}44`, borderRadius: 8, padding: "14px 18px" }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: colors[quartile], marginBottom: 4 }}>{labels[quartile]}</div>
        <div style={{ fontSize: "0.84rem", color: textCol, lineHeight: 1.7 }}>{descriptions[quartile]}</div>
      </div>

      {/* Survivorship Bias */}
      <div style={{ marginTop: 20, background: `rgba(245,166,35,.07)`, border: `1px solid rgba(245,166,35,.2)`, borderRadius: 8, padding: "18px 20px" }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: gold, marginBottom: 10 }}>⚠️ Survivorship Bias</div>
        <div style={{ fontSize: "0.85rem", color: textCol, lineHeight: 1.75, marginBottom: 10 }}>
          Category averages and quartile rankings look <strong style={{ color: gold }}>artificially high</strong> because poorly performing funds that were shut down or merged are removed from the database.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            { label: "What we see", val: "Only surviving funds — the winners", icon: "✅", color: green },
            { label: "What's hidden", val: "Merged/liquidated funds — the losers", icon: "❌", color: red },
          ].map((item, i) => (
            <div key={i} style={{ background: navy4, borderRadius: 6, padding: "10px 14px" }}>
              <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: item.color, marginBottom: 4 }}>{item.icon} {item.label}</div>
              <div style={{ fontSize: "0.78rem", color: muted }}>{item.val}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Comparison Table ──
function MeasuresCompareTable() {
  const rows = [
    { measure: "Standard Deviation", what: "Total Volatility", unit: "%", higher: "More risky / volatile", usage: "Fund Facts risk rating", color: blue2 },
    { measure: "Beta", what: "Market Sensitivity", unit: "Ratio", higher: "More volatile than benchmark", usage: "Portfolio construction", color: gold },
    { measure: "Sharpe Ratio", what: "Return per unit of risk", unit: "Ratio", higher: "Better risk-adjusted return ✓", usage: "Cross-fund comparison", color: green },
    { measure: "TWRR", what: "Manager performance", unit: "%", higher: "Better manager skill", usage: "Fund Facts / advertising", color: purple },
    { measure: "MWRR", what: "Client experience", unit: "%", higher: "Better personal timing", usage: "Client statements (CRM2)", color: blue2 },
  ];
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, overflow: "hidden", margin: "28px 0" }}>
      <div style={{ padding: "14px 20px", borderBottom: `1px solid ${border}`, fontFamily: "Georgia,serif", fontSize: "0.9rem", color: white }}>📋 Performance Measures — Quick Reference</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: navy4 }}>
              {["Measure", "Measures", "Unit", "Higher Means", "Used For"].map(h => (
                <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, borderBottom: `1px solid ${border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${border}` }}>
                <td style={{ padding: "11px 14px" }}><span style={{ fontFamily: "monospace", fontSize: "0.78rem", fontWeight: 700, color: row.color }}>{row.measure}</span></td>
                <td style={{ padding: "11px 14px", fontSize: "0.8rem", color: textCol }}>{row.what}</td>
                <td style={{ padding: "11px 14px", fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>{row.unit}</td>
                <td style={{ padding: "11px 14px", fontSize: "0.78rem", color: textCol }}>{row.higher}</td>
                <td style={{ padding: "11px 14px", fontSize: "0.78rem", color: muted }}>{row.usage}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Quiz ──
const questions = [
  { q: "Which rate of return is required for client account statements under CRM2 regulations?", opts: ["Time-Weighted Rate of Return (TWRR)", "Dollar-Weighted (Money-Weighted) Rate of Return", "Simple Average Return", "The Sharpe Ratio"], correct: 1, fb: { y: "Correct! CRM2 requires client statements to show the Dollar-Weighted (Money-Weighted) Return — reflecting the client's actual personal experience based on when they added or withdrew money.", n: "Not quite. CRM2 mandates the MWRR on client statements — it accounts for the timing of the client's own cash flows. TWRR is used for Fund Facts and advertising." } },
  { q: "If a fund has a Beta of 1.5, how is it expected to behave?", opts: ["It will move exactly in line with the market", "It will be 50% less volatile than the market", "It will be 50% more volatile than the market", "It will generate a guaranteed 1.5% return"], correct: 2, fb: { y: "Correct! Beta 1.5 means the fund is expected to move 50% more than the benchmark — if the market rises 10%, the fund rises ~15%; if the market falls 10%, the fund falls ~15%.", n: "Not quite. Beta 1.5 = 50% MORE volatile than the market. Beta < 1.0 means less volatile. Beta > 1.0 means more volatile." } },
  { q: "What is the most appropriate benchmark for a Canadian Equity Mutual Fund?", opts: ["S&P 500", "FTSE Canada Universe Bond Index", "S&P/TSX Composite Index", "MSCI World Index"], correct: 2, fb: { y: "Correct! The S&P/TSX Composite Index tracks ~250 large Canadian companies and is the primary benchmark for Canadian equity funds.", n: "Not quite. The S&P/TSX Composite is the Canadian equity benchmark. The S&P 500 is U.S. equity. MSCI World is global equity. FTSE Canada is for bonds." } },
  { q: "A high Sharpe Ratio indicates:", opts: ["The fund took high risks to achieve high returns", "The fund achieved high returns relative to the risk taken", "The fund has a high MER", "The fund tracks its benchmark index perfectly"], correct: 1, fb: { y: "Correct! The Sharpe Ratio measures return per unit of risk. A high ratio means the manager generated strong excess returns without taking excessive risk — efficient performance.", n: "Not quite. A high Sharpe Ratio is positive — it means the return earned was high relative to the risk taken. A fund could have high returns but a low Sharpe if it took excessive risk to get there." } },
  { q: "'Survivorship Bias' causes historical fund performance averages to appear:", opts: ["Lower than reality", "Higher than reality", "More volatile than reality", "Less correlated than reality"], correct: 1, fb: { y: "Correct! When poorly performing funds are shut down and removed from databases, only the winners remain. This makes average fund performance look better than it actually was across the full universe.", n: "Not quite. Survivorship bias inflates averages — the losers are deleted, so only winners are counted. This makes performance data look unrealistically good." } },
  { q: "Time-Weighted Rate of Return (TWRR) is best used to evaluate:", opts: ["The client's timing of deposits and withdrawals", "The Portfolio Manager's performance", "The inflation rate of the economy", "The tax efficiency of the fund"], correct: 1, fb: { y: "Correct! TWRR eliminates the impact of client cash flows — isolating the manager's actual investment decisions. This is why it's the industry standard for Fund Facts and advertising.", n: "Not quite. TWRR measures the manager's skill by stripping out the effect of client deposits/withdrawals. The MWRR reflects the client's personal experience." } },
  { q: "Standard Deviation is primarily a measure of:", opts: ["Manager skill", "Volatility (Total Risk)", "Liquidity", "Credit quality"], correct: 1, fb: { y: "Correct! Standard deviation measures how widely a fund's returns vary around its average — high SD means volatile returns, low SD means consistent returns. It's used to set the Fund Facts risk rating.", n: "Not quite. Standard deviation = volatility = total risk. Manager skill might be measured by alpha. Liquidity and credit quality are separate risk measures." } },
  { q: "If a fund is in the '1st Quartile', it means:", opts: ["It is in the bottom 25% of its category", "It performed better than 75% of its peers", "It returned exactly 1% for the year", "It is a money market fund by definition"], correct: 1, fb: { y: "Correct! 1st Quartile = top 25% of performers. A 1st quartile fund beat at least 75% of comparable funds in the same category over the period.", n: "Not quite. 1st Quartile is the TOP 25% — best performers. 4th Quartile is the bottom 25%. Don't confuse the ranking order." } },
  { q: "Returns for periods of less than one year must be reported as:", opts: ["Annualized (Compounded) returns", "Simple Returns (not annualized)", "Inflation-adjusted returns", "They can be ignored for short periods"], correct: 1, fb: { y: "Correct! Short-period returns must be simple (not annualized) to prevent projecting short-term luck into the future. A fund up 5% in 3 months does not necessarily mean 20% per year.", n: "Not quite. Returns for periods under one year must be simple returns — NOT annualized. Annualizing short periods can mislead by projecting unsustainable short-term gains." } },
  { q: "The Information Ratio measures:", opts: ["A fund's ability to generate excess return relative to its tracking error", "The fund's MER", "The speed of information flow in markets", "The dividend yield of the portfolio"], correct: 0, fb: { y: "Correct! The Information Ratio = (Fund Return − Benchmark Return) ÷ Tracking Error. It measures how consistently and efficiently a manager generates active returns (alpha) above their benchmark.", n: "Not quite. The Information Ratio measures active management efficiency — how much excess return (alpha) was generated per unit of active risk (tracking error). Higher is better." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = questions.length;
  function answer(idx) { if (answered[current] !== undefined) return; setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } }); }
  function next() { if (current < total - 1) setCurrent(current + 1); else setDone(true); }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }
  const q = questions[current], ans = answered[current];
  const pct = Math.round(score / total * 100);

  return (
    <div style={{ background: navy2, border: `1px solid ${border}`, borderRadius: 12, padding: 36, marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem", color: white }}>Chapter 14 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>
      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", color: white, lineHeight: 1.55, marginBottom: 22 }}>{q.q}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i, isCorrect = i === q.correct;
              let bg = navy3, bdr = border, col = textCol;
              if (ans) { if (isCorrect) { bg = "rgba(34,197,94,.08)"; bdr = green; col = "#4ade80"; } else if (isChosen) { bg = "rgba(239,68,68,.08)"; bdr = "#ef4444"; col = "#f87171"; } }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>{["A","B","C","D"][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 20, background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: navy2 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.5rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. You've mastered fund performance measurement." : score >= 8 ? "Excellent — review any missed questions." : score >= 6 ? "Good start. Revisit TWRR vs. MWRR and the Sharpe Ratio." : "Focus on benchmarks, risk measures, TWRR vs. MWRR, and survivorship bias."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// ── Glossary ──
const glossary = [
  { term: "Benchmark Index", def: "A reference index used to evaluate a fund's performance. Must match the fund's asset class (e.g., S&P/TSX for Canadian equity)." },
  { term: "Relative Performance", def: "Comparing a fund's return to its benchmark. A fund earning 8% when the benchmark earns 12% has underperformed — despite a positive return." },
  { term: "Peer Group Comparison", def: "Comparing a fund to other funds in the same category (e.g., all Canadian Equity funds). Used for quartile rankings." },
  { term: "Simple Rate of Return", def: "[(Ending Value − Beginning Value + Distributions) ÷ Beginning Value] × 100. Used for single periods (< 1 year)." },
  { term: "CAGR", def: "Compound Annual Growth Rate — the smooth annual growth rate over a multi-year period. Required for returns > 1 year." },
  { term: "TWRR", def: "Time-Weighted Rate of Return — measures the fund/manager's performance by eliminating the effect of client cash flows. Used in Fund Facts." },
  { term: "MWRR", def: "Money-Weighted (Dollar-Weighted) Rate of Return — reflects the client's actual experience including cash flow timing. Required on client statements (CRM2)." },
  { term: "CRM2", def: "Client Relationship Model Phase 2 — regulations requiring investment dealers to report personal rates of return (MWRR) on client account statements." },
  { term: "Standard Deviation", def: "Measures return volatility — how widely a fund's returns vary around its average. Used to set the Fund Facts risk rating (Low to High)." },
  { term: "Beta", def: "Measures a fund's sensitivity to benchmark movements. Beta > 1 = more volatile; Beta < 1 = less volatile; Beta = 1.0 = tracks market." },
  { term: "Sharpe Ratio", def: "(Fund Return − Risk-Free Rate) ÷ Standard Deviation. Measures excess return per unit of risk. Higher is always better." },
  { term: "Risk-Free Rate", def: "The return on a risk-free investment (typically T-bills). Used as the baseline in the Sharpe Ratio formula." },
  { term: "Excess Return (Alpha)", def: "The return above the benchmark or risk-free rate. Positive alpha = the manager added value beyond the market return." },
  { term: "Real Return", def: "Nominal Return − Inflation Rate. The actual increase in purchasing power after accounting for inflation." },
  { term: "Quartile Ranking", def: "Funds split into four groups by performance. 1st Quartile = top 25%; 4th Quartile = bottom 25%." },
  { term: "1st Quartile", def: "Top 25% of funds in a peer category — better than 75% of peers. Best performers." },
  { term: "4th Quartile", def: "Bottom 25% of funds in a peer category — worst performers. High risk of being merged or closed." },
  { term: "Survivorship Bias", def: "Historical averages look artificially high because failing funds are removed from databases. Only 'survivors' remain in the data." },
  { term: "Information Ratio", def: "(Fund Return − Benchmark Return) ÷ Tracking Error. Measures how consistently a manager generates alpha per unit of active risk." },
  { term: "Tracking Error", def: "The standard deviation of the difference between a fund's return and its benchmark. High tracking error = larger active bets." },
];

// ── Main Export ──
export default function Chapter14() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement;
      setProgress((h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100);
    };
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const tocItems = [
    ["01", "Benchmarking — Matching Funds to Indexes"],
    ["02", "Rates of Return — Simple, CAGR, TWRR vs. MWRR"],
    ["03", "Risk-Adjusted Measures — Std Dev, Beta, Sharpe"],
    ["04", "Quartile Rankings & Survivorship Bias"],
    ["📋", "Performance Measures Quick-Reference Table"],
    ["🧮", "Simple Return, Sharpe Ratio & Real Return Calculators"],
    ["✦", "Knowledge Check Quiz"],
    ["✦", "Key Terms Glossary"],
  ];

  return (
    <div style={{ background: navy, minHeight: "100vh", fontFamily: "'Nunito Sans', sans-serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, height: 3, background: `linear-gradient(90deg,${gold},${blue})`, width: `${progress}%`, zIndex: 9999, transition: "width 0.15s ease" }} />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 80px" }}>

        <Reveal>
          <div style={{ padding: "80px 0 56px", borderBottom: `1px solid ${border}`, marginBottom: 56 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.25)", color: gold, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", borderRadius: 100, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: gold, display: "inline-block" }} />
              IFC/CSC · Chapter 14
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(2.2rem,5vw,3.4rem)", fontWeight: 900, color: white, lineHeight: 1.15, marginBottom: 20 }}>
              Fund Performance <span style={{ color: gold }}>Measurement</span>
            </h1>
            <p style={{ fontSize: "1.05rem", color: muted, maxWidth: 600, lineHeight: 1.8, marginBottom: 32 }}>
              Benchmarking, rates of return, risk-adjusted measures, and quartile rankings — the tools used to evaluate whether a fund manager actually earned their fees.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 24, borderTop: `1px solid ${border}` }}>
              {[["📖","4","Core Topics"],["⏱","~18 min","read"],["🎯","10","practice questions"],["🧮","3","calculators"],["📊","3","interactive tools"]].map(([icon,val,lbl],i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: blue }}>{icon}</span>
                  <span style={{ fontSize: "0.82rem", color: muted }}><strong style={{ color: textCol }}>{val}</strong> {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, borderRadius: 8, padding: "28px 32px", marginBottom: 56 }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "0.85rem", fontWeight: 700, color: gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>📋 In This Chapter</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {tocItems.map(([num, label], i) => (
                <li key={i} style={{ padding: "6px 0", borderBottom: i < tocItems.length - 1 ? `1px solid ${border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: muted, width: 24, flexShrink: 0 }}>{num}</span>
                  <span style={{ color: textCol, fontSize: "0.9rem", fontWeight: 600 }}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* S1 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 01</span><span style={ss.badge}>Benchmarking</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Benchmarking Fund Performance</h2>
            <p style={ss.prose}>
              A fund's raw return means nothing without context. A <span style={{ color: gold, fontWeight: 600 }}>benchmark index</span> representing the same asset class is required to judge whether the manager added or destroyed value. A fund earning 8% when its benchmark earned 12% has <em>underperformed</em> — despite the positive number:
            </p>
          </Reveal>
          <Reveal delay={0.1}><BenchmarkTable /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="📌">
              <strong>Matching Benchmark to Fund Type is an exam favourite.</strong> Canadian Equity → S&P/TSX Composite. U.S. Equity → S&P 500. Global Equity → MSCI World. Canadian Bonds → FTSE Canada Universe Bond Index. Using the wrong benchmark makes performance comparison meaningless.
            </Callout>
          </Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S2 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 02</span><span style={ss.badge}>Rates of Return</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Calculating Rates of Return</h2>
            <p style={ss.prose}>
              Regulators require standardized return reporting. The most critical distinction is <span style={{ color: gold, fontWeight: 600 }}>TWRR vs. MWRR</span> — two different answers to "how did this fund perform?" — one for the manager, one for the client:
            </p>
          </Reveal>
          <Reveal delay={0.1}><TWRRvsMWRR /></Reveal>
          <Reveal delay={0.2}><SimpleReturnCalc /></Reveal>
          <Reveal delay={0.3}>
            <Callout icon="📏" accent={blue}>
              <strong style={{ color: blue2 }}>CAGR Rule:</strong> Returns for periods <strong>longer than 1 year</strong> must be expressed as a <strong>compounded annual rate</strong> (CAGR). Returns for periods <strong>less than 1 year</strong> must be <strong>simple (not annualized)</strong> — to prevent projecting short-term luck into the future as if it would continue all year.
            </Callout>
          </Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S3 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 03</span><span style={ss.badge}>Risk-Adjusted Measures</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Risk-Adjusted Performance Measures</h2>
            <p style={ss.prose}>
              A 15% return sounds great — unless the fund took enormous risk to achieve it. Risk-adjusted measures <span style={{ color: gold, fontWeight: 600 }}>level the playing field</span> by factoring in volatility, market sensitivity, and the risk-free alternative:
            </p>
          </Reveal>
          <Reveal delay={0.1}><RiskMeasuresExplorer /></Reveal>
          <Reveal delay={0.2}><SharpeCalc /></Reveal>
          <Reveal delay={0.3}><RealReturnCalc /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S4 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 04</span><span style={ss.badge}>Quartile Rankings</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Quartile Rankings &amp; Survivorship Bias</h2>
            <p style={ss.prose}>
              Funds are ranked against their peers and divided into four equal groups. A 1st-quartile fund beat <span style={{ color: gold, fontWeight: 600 }}>75% of comparable funds</span>. But beware — peer group averages are distorted upward by survivorship bias:
            </p>
          </Reveal>
          <Reveal delay={0.1}><QuartileViz /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Reference Table */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Quick Reference</span><span style={ss.badge}>All Measures</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Performance Measures at a Glance</h2>
            <p style={ss.prose}>All five key measures from Chapter 14 — what they measure, what a higher value means, and where each is used:</p>
          </Reveal>
          <Reveal delay={0.1}><MeasuresCompareTable /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Quiz */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Knowledge Check</span><span style={ss.badge}>10 Questions</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Test Your Understanding</h2>
            <p style={ss.prose}>10 questions covering benchmarks, TWRR vs. MWRR, Sharpe Ratio, standard deviation, quartile rankings, and survivorship bias from Chapter 14.</p>
          </Reveal>
          <Reveal delay={0.1}><Quiz /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Glossary */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Quick Reference</span><span style={ss.badge}>Key Terms</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Glossary</h2>
            <p style={ss.prose}>All essential terms from Chapter 14 — performance measurement, risk metrics, and return calculation methods.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10, margin: "28px 0" }}>
              {glossary.map((g, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 20px", transition: "border-color 0.2s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(245,166,35,.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}>
                  <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
                  <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

      </div>
    </div>
  );
}
